import {
  AggregationData,
  AggregationField,
  GenericAggregations
} from "@/services/api/types";

/**
 * Exact per-group figures for a grouped table. `total` and `sums` cover the
 * whole group on the server, not just the rows paged in so far.
 */
export interface GroupSummary {
  total: number;
  sums: Record<string, number>;
  /**
   * Rows in the group that actually carry each field. SQL AVG ignores NULLs,
   * so an average has to divide by this rather than by `total`.
   */
  counts: Record<string, number>;
  isLoading: boolean;
}

export type GroupSummaryMap = Record<string, GroupSummary>;

/** The server labels a null group this way; the table keys an empty group on "". */
const UNKNOWN_FIELD_VALUE = "Unknown";

const fieldOf = (fields: AggregationField<string>[], name: string) =>
  fields.find((field) => field.name === name)?.aggregations;

/**
 * One grouped aggregations response already describes every group: the server
 * groups over the whole filtered set, so each entry is a distinct value of the
 * group field with its own COUNT(*) and sums. No per-group request needed.
 */
export const buildGroupSummaries = (
  aggregations: GenericAggregations<string> | undefined,
  sumFields: string[],
  isLoading: boolean
): GroupSummaryMap => {
  const summaries: GroupSummaryMap = {};

  (aggregations?.data ?? []).forEach((group) => {
    const key =
      group.fieldValue === UNKNOWN_FIELD_VALUE ? "" : group.fieldValue;
    const sums: Record<string, number> = {};
    const counts: Record<string, number> = {};
    sumFields.forEach((field) => {
      const aggregations = fieldOf(group.fields, field);
      sums[field] = aggregations?.sum || 0;
      counts[field] = aggregations?.count || 0;
    });
    // The null sentinel and a literal empty value both key on "", so a response
    // carrying both lands twice on one group — added, as rekeyGroupSummaries
    // adds its own collisions, rather than the second silently replacing the
    // first and dropping a group's rows from the subtotal.
    const running = summaries[key];
    if (!running) {
      summaries[key] = {
        total: group.total_count ?? 0,
        sums,
        counts,
        isLoading
      };
      return;
    }
    running.total += group.total_count ?? 0;
    sumFields.forEach((field) => {
      running.sums[field] = (running.sums[field] || 0) + sums[field];
      running.counts[field] = (running.counts[field] || 0) + counts[field];
    });
  });

  return summaries;
};

/**
 * Every distinct value of the group field in the filtered set, as the grouped
 * response already enumerated them — one entry per value, so the table never
 * needs a separate distinct-values request to learn which groups exist. The
 * null group names no record to resolve, so it is left out.
 */
export const groupFieldValues = (
  aggregations: GenericAggregations<string> | undefined
): string[] =>
  (aggregations?.data ?? [])
    .map((group) => group.fieldValue)
    .filter((fieldValue) => !!fieldValue && fieldValue !== UNKNOWN_FIELD_VALUE);

/**
 * Collapses a grouped response back into the single row an ungrouped one
 * returns, so figures covering the whole filtered set — the KPI bars — read the
 * same numbers whether or not the table happens to be grouped. Sums and counts
 * add; an average is recomputed from them rather than averaged, since each
 * group's average carries its own weight.
 */
export const mergeAggregationGroups = (
  aggregations: GenericAggregations<string> | undefined
): AggregationData<string> | undefined => {
  const groups = aggregations?.data ?? [];
  if (groups.length <= 1) return groups[0];

  const merged = new Map<string, AggregationField<string>>();
  let totalCount = 0;

  groups.forEach((group) => {
    totalCount += group.total_count ?? 0;
    group.fields.forEach((field) => {
      const running = merged.get(field.name);
      if (!running) {
        merged.set(field.name, {
          name: field.name,
          aggregations: { ...field.aggregations }
        });
        return;
      }
      const a = running.aggregations;
      const b = field.aggregations;
      a.sum = (a.sum || 0) + (b.sum || 0);
      a.count = (a.count || 0) + (b.count || 0);
      a.min = Math.min(a.min ?? b.min, b.min ?? a.min);
      a.max = Math.max(a.max ?? b.max, b.max ?? a.max);
      a.avg = a.count ? a.sum / a.count : 0;
    });
  });

  return {
    fieldValue: "all",
    total_count: totalCount,
    fields: Array.from(merged.values())
  };
};

/**
 * Re-keys summaries from the value the server grouped on to the value the table
 * groups on — an id to the name the column shows. Two server groups can land on one table group (two company
 * records sharing a name), so colliding keys are added together rather than
 * overwritten.
 */
export const rekeyGroupSummaries = (
  summaries: GroupSummaryMap,
  keyOf: (fieldValue: string) => string
): GroupSummaryMap => {
  const rekeyed: GroupSummaryMap = {};

  Object.entries(summaries).forEach(([fieldValue, summary]) => {
    const key = keyOf(fieldValue);
    const running = rekeyed[key];
    if (!running) {
      rekeyed[key] = {
        total: summary.total,
        sums: { ...summary.sums },
        counts: { ...summary.counts },
        isLoading: summary.isLoading
      };
      return;
    }
    running.total += summary.total;
    running.isLoading = running.isLoading || summary.isLoading;
    Object.entries(summary.sums).forEach(([field, value]) => {
      running.sums[field] = (running.sums[field] || 0) + value;
    });
    Object.entries(summary.counts).forEach(([field, value]) => {
      running.counts[field] = (running.counts[field] || 0) + value;
    });
  });

  return rekeyed;
};

/**
 * The display value each id resolves to, harvested from the rows already
 * loaded. A group whose rows have not been paged in yet is not in here, so its
 * summary keeps the id as its key — matching no group rather than the wrong
 * one — until a page carries a row of it.
 */
export const buildDisplayValueById = <T extends object>(
  rows: T[],
  idKey: string,
  displayKey: string
): Record<string, string> => {
  const byId: Record<string, string> = {};
  rows.forEach((row) => {
    const values = row as Record<string, unknown>;
    const id = values[idKey];
    const display = values[displayKey];
    if (id == null || display == null) return;
    byId[String(id)] = String(display);
  });
  return byId;
};
